import { useCallback, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { GridLoader } from "@fortex/api";
import { readField } from "@fortex/api";
import { Button, DataGrid, Dialog, Switch, csvOf, downloadText, englishGridLabels, errorMessage, hungarianGridLabels } from "@fortex/map";
import type { GridColumn, GridGroupOf } from "@fortex/map";
import { useApp } from "../../app/context";
import { DetailPane, useDetailMode } from "./DetailPane";

export type FieldErrors = Record<string, string>;

export type CrudEditing<TDraft> = { draft: TDraft; isNew: boolean; key: string | null };

export type CrudPageProps<TRow, TDraft> = {
  dataFxmap: string;
  columns: GridColumn<TRow>[];
  rowKey: (row: TRow) => string;
  load: GridLoader<TRow>;
  defaultSortField?: string;
  quickFields?: string[];
  canEdit: boolean;
  newLabel: string;
  emptyDraft: () => TDraft;
  toDraft: (row: TRow) => TDraft | Promise<TDraft>;
  detailTitle: (editing: CrudEditing<TDraft>) => string;
  detailSubtitle?: (editing: CrudEditing<TDraft>) => string | null;
  form: (draft: TDraft, setDraft: (draft: TDraft) => void, errors: FieldErrors, editing: CrudEditing<TDraft>) => ReactNode;
  validate?: (draft: TDraft, isNew: boolean) => FieldErrors;
  save: (draft: TDraft, isNew: boolean) => Promise<unknown>;
  remove?: ((draft: TDraft) => Promise<void>) | null;
  deleteText: (draft: TDraft) => string;
  savedText: string;
  deletedText: string;
  toolbar?: ReactNode;
  groupBy?: GridGroupOf<TRow> | null;
  groupSwitchLabel?: string | null;
  exportName: string;
  detailFooter?: ReactNode;
  reloadToken?: number;
  onReload?: () => void;
};

export function hasErrors(errors: FieldErrors): boolean {
  return Object.keys(errors).length > 0;
}

export function CrudPage<TRow, TDraft>({
  dataFxmap,
  columns,
  rowKey,
  load,
  defaultSortField,
  quickFields,
  canEdit,
  newLabel,
  emptyDraft,
  toDraft,
  detailTitle,
  detailSubtitle,
  form,
  validate,
  save,
  remove = null,
  deleteText,
  savedText,
  deletedText,
  toolbar,
  groupBy = null,
  groupSwitchLabel = null,
  exportName,
  detailFooter,
  reloadToken = 0,
  onReload
}: CrudPageProps<TRow, TDraft>) {
  const { labels, lang, toast } = useApp();
  const gridLabels = lang === "en" ? englishGridLabels : hungarianGridLabels;
  const [editing, setEditing] = useState<CrudEditing<TDraft> | null>(null);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [ownReload, setOwnReload] = useState(0);
  const [rows, setRows] = useState<TRow[]>([]);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [selectedRows, setSelectedRows] = useState<TRow[]>([]);
  const [grouped, setGrouped] = useState(true);
  const [mode, setMode] = useDetailMode();
  const onRowsChange = useCallback((next: TRow[]) => setRows(next), []);
  const reload = () => {
    setOwnReload(a => a + 1);
    onReload?.();
  };

  const close = () => {
    setEditing(null);
    setErrors({});
  };

  const openRow = async (row: TRow) => {
    try {
      const draft = await toDraft(row);

      setErrors({});
      setEditing({ draft, isNew: false, key: rowKey(row) });
    } catch (reason) {
      toast("danger", errorMessage(reason, labels));
    }
  };

  const openNew = () => {
    setErrors({});
    setEditing({ draft: emptyDraft(), isNew: true, key: null });
  };

  const commit = async () => {
    if (editing === null) return;

    const found = validate ? validate(editing.draft, editing.isNew) : {};

    setErrors(found);

    if (hasErrors(found)) return;

    setSaving(true);

    try {
      await save(editing.draft, editing.isNew);
      toast("success", savedText);
      close();
      reload();
    } catch (reason) {
      toast("danger", errorMessage(reason, labels));
    } finally {
      setSaving(false);
    }
  };

  const drop = async () => {
    if (editing === null || remove === null) return;

    setDeleting(true);

    try {
      await remove(editing.draft);
      toast("success", deletedText);
      setConfirmDelete(false);
      close();
      reload();
    } catch (reason) {
      toast("danger", errorMessage(reason, labels));
    } finally {
      setDeleting(false);
    }
  };

  const exportRows = () => {
    const source = selectedRows.length > 0 ? selectedRows : rows;
    const headers = columns.map(a => a.header);
    const records = source.map(row => Object.fromEntries(columns.map(column => [column.header, readField(row, column.field ?? column.key)])));

    downloadText(`${exportName}.csv`, csvOf(headers, records));
  };

  const gridToolbar = (
    <>
      {canEdit ? <Button kind="primary" size="sm" icon="plus" label={newLabel} onClick={openNew} data-fxmap="crud-new" /> : null}
      <Button kind="ghost" size="sm" icon="redo" label={labels.refresh} onClick={reload} data-fxmap="crud-refresh" />
      <Button kind="ghost" size="sm" icon="save" label={labels.exportCsv} disabled={rows.length === 0} onClick={exportRows} data-fxmap="crud-export" />
      {groupBy !== null && groupSwitchLabel !== null ? <Switch checked={grouped} onChange={setGrouped} label={groupSwitchLabel} showLabel className="fxapp-group-switch" /> : null}
      {toolbar}
    </>
  );

  const activeGroupBy = useMemo(() => (grouped ? groupBy : null), [grouped, groupBy]);

  const detail =
    editing === null ? null : (
      <DetailPane
        mode={mode}
        title={detailTitle(editing)}
        subtitle={detailSubtitle ? detailSubtitle(editing) : null}
        onClose={close}
        onToggleMode={() => setMode(mode === "side" ? "modal" : "side")}
        footer={
          <>
            {remove !== null && !editing.isNew && canEdit ? <Button kind="danger" size="sm" icon="trash" label={labels.delete} onClick={() => setConfirmDelete(true)} disabled={saving} data-fxmap="crud-delete" /> : <span />}
            <span className="fxapp-form-actions">
              <Button kind="ghost" size="sm" label={labels.cancel} onClick={close} disabled={saving} />
              {canEdit ? <Button kind="primary" size="sm" icon="check" label={labels.save} busy={saving} onClick={() => void commit()} data-fxmap="crud-save" /> : null}
            </span>
          </>
        }
        extraFooter={detailFooter}
      >
        {form(editing.draft, draft => setEditing({ ...editing, draft }), errors, editing)}
      </DetailPane>
    );

  return (
    <div className={["fxapp-crud", editing !== null && mode === "side" ? "fxapp-crud-with-detail" : ""].filter(Boolean).join(" ")} data-fxmap={dataFxmap}>
      <section className="fxapp-crud-list">
        <DataGrid<TRow>
          columns={columns}
          rowKey={rowKey}
          load={load}
          labels={gridLabels}
          defaultSortField={defaultSortField}
          quickFields={quickFields}
          toolbar={gridToolbar}
          reloadToken={reloadToken + ownReload}
          selectable
          selectedKeys={selected}
          onSelectionChange={(keys, picked) => {
            setSelected(keys);
            setSelectedRows(picked);
          }}
          activeKey={editing?.key ?? null}
          onRowClick={row => void openRow(row)}
          groupBy={activeGroupBy}
          onRowsChange={onRowsChange}
          dataFxmap={`${dataFxmap}-grid`}
        />
      </section>
      {detail}
      <Dialog open={confirmDelete} title={labels.confirmDelete} text={editing === null ? null : deleteText(editing.draft)} confirmLabel={labels.delete} cancelLabel={labels.cancel} danger busy={deleting} onConfirm={() => void drop()} onCancel={() => setConfirmDelete(false)} dataFxmap="crud-delete-dialog" />
    </div>
  );
}
